import {
  RightMainContainer,
  PrimaryHeading,
  ListIconWrapper,
  ListCard,
  InputField,
  HeaderContainer,
  BodyContainer,
  MainButton,
  TemplateNotFound,
  LoadingListCards,
  Loader,
  FiltersList,
} from "commonUI";
import { NotesIcon, AddIcon } from "allIcons";
import { Children, useDeferredValue, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  useAddNoteTemplate,
  useDeleteNoteTemplate,
  useTemplateNotes,
} from "api/templateLibrary/notes";
import { parse, search } from "helpers";
import { useUserPermissions } from "hooks/user";
import { libraryItemFiltering, libraryItemSorting } from "filterAndSort/templateLibrary";
import { useTags } from "api/siteSettings/Tags";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function Notes() {
  const {
    data: notes,
    isLoading: notesLoading,
    error: notesError,
  } = useTemplateNotes();

  const { mutate: deleteMutation, isLoading: deleteMutationIsLoading } =
    useDeleteNoteTemplate();
  const { mutate: addNote, isLoading: addingNote } = useAddNoteTemplate();
  const { add_new_templates, edit_templates, delete_templates } = useUserPermissions();
  const { data: tags } = useTags();
  const [searchText, setSearchText] = useState("");
  const defferedSearch = useDeferredValue(searchText);
  const [searchResult, setSearchResult] = useState([]);
  const [filterData, setFilterData] = useState({});

  useEffect(() => {
    setSearchResult(notes.filter((task) => search(task.title, defferedSearch)));
  }, [defferedSearch, notes]);

  const applySorting = (order, orderBy) => {
    setSearchResult((prevSearchResult) =>
      libraryItemSorting(order, orderBy, prevSearchResult)
    );
  };

  const applyFilter = ({
    selectedFilter,
    selectedTags,
    selectedLibrarySource,
    ...data
  }) => {
    setFilterData({
      ...data,
      selectedTags,
      selectedLibrarySource,
      selectedFilter,
    });
    setSearchResult(
      libraryItemFiltering(
        selectedFilter,
        selectedTags,
        selectedLibrarySource,
        notes
      )
    );
  };

  return (
    <>
      <RightMainContainer>
        {deleteMutationIsLoading || addingNote ? <Loader loader="block" /> : ""}
        <HeaderContainer>
          <PrimaryHeading>{t("libraryTemplate.notes.title")}</PrimaryHeading>
          {add_new_templates ? <Link to={`/${ModulePrefixes.TEMPLATE_LIBRARY}/notes/add`}>
            <MainButton>
              <AddIcon fill="white" />
              {t("button.add")}
            </MainButton>
          </Link> : ""}
        </HeaderContainer>
        <BodyContainer>
        <InputField
            placeholder={t("input.search.placeholder")}
            searchIcon={true}
            filter={true}
            value={searchText}
            setValue={(val) => setSearchText(val)}
            tags={tags ?? []}
            onApplyFilter={applyFilter}
            applySorting={applySorting}
            filterData={filterData}
            showLibrarySource={false}
          />
          <FiltersList filterData={filterData} updateFilter={applyFilter} />
          {notesError ? (
            <p className="body-text text-center">{t("libraryTemplate.notes.error.load")}</p>
          ) : notesLoading ? (
            <LoadingListCards />
          ) : searchResult.length > 0 ? (
            Children.toArray(
              searchResult.map((note, index) => (
                <ListCard
                  data={note.title}
                  duplicate={add_new_templates}
                  editIcon={edit_templates}
                  link={edit_templates ? `/${ModulePrefixes.TEMPLATE_LIBRARY}/notes/${note.id}` : ""}
                  strictLink={true}
                  onDuplicate={(data) => {
                    addNote({
                      title: `Copy of ${note.title}`,
                      message: note.message,
                      tags: parse(note.tags),
                      requested_from: parse(note.requested_from),
                    });
                  }}
                  deleteIcon={!delete_templates}
                  onDelete={() => {
                    if (!delete_templates) return;
                    deleteMutation(note.id);
                  }}
                  icon={
                    <ListIconWrapper>
                      <NotesIcon fill="white" />
                    </ListIconWrapper>
                  }
                  deleteTitle={t("libraryTemplate.delete.title")}
                  deleteButtonText={t("libraryTemplate.delete.button")}
                  deleteMessage={t("libraryTemplate.delete.message")}
                />
              ))
            )
          ) : (
            <TemplateNotFound label={t("libraryTemplate.notes.title")} link={add_new_templates ? "add" : ""} linkLabel={t("libraryTemplate.note")} />
          )}
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
